import { Autocomplete, ComboBoxOption, Flex, Loader, useTheme } from "@aws-amplify/ui-react";
import { RawResult } from 'leaflet-geosearch/dist/providers/openStreetMapProvider.js';
import React, { ChangeEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaAngleDown } from "react-icons/fa6";


import { SearchResult } from "leaflet-geosearch/dist/providers/provider.js";
import GeosearchUtils from "../../utils/GeosearchUtils";
import InputSanitizationUtils from "../../utils/InputSanitizationUtils";
import './LocationSearch.css';

interface LocationSearchParams {
    isMapWindowVisible: boolean // Only call location API if map window is visible
    onSearchLocationSelected: (locationSearchResult: SearchResult<RawResult>) => void
}

const DEBOUNCE_TIMEOUT_MS: number = 2000

let lastTrimmedSearchQuery: string = ''

let locationSearchFullResult: SearchResult<RawResult>[] = []

const LocationSearch: React.FC<LocationSearchParams> = ({ isMapWindowVisible, onSearchLocationSelected }) => {

    const [searchQuery, setSearchQuery] = useState<string>("")
    const [isSearchQueryValid, setIsSearchQueryValid] = useState<boolean>(false)
    const [isSearchOptionsLoading, setIsSearchOptionsLoading] = useState<boolean>(false)
    const [searchOptions, setSearchOptions] = useState<ComboBoxOption[]>([])
    const [searchFieldInError, setSearchFieldInError] = useState<boolean>(false)
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)
    const [wasLastInputSanitized, setWasLastInputSanitized] = useState<boolean>(false)

    const { t } = useTranslation();
    const { tokens } = useTheme();

    const geoSearchUtils = new GeosearchUtils();
    const autocompleteRef = useRef<HTMLInputElement>(null);

    let activeTimeoutId: NodeJS.Timeout

    React.useEffect(() => {
        // Debounce input value - do not call searchLocation API for each change of input
        activeTimeoutId = setTimeout(() => {
            processDebouncedSearchQuery(searchQuery)
        }, DEBOUNCE_TIMEOUT_MS);

        return () => clearTimeout(activeTimeoutId)
    }, [searchQuery])

    const processDebouncedSearchQuery = async (searchQuery: string) => {
        if (!isMapWindowVisible) {
            console.warn("Do NOT do anything, window is not visible!");
            return;
        }

        // Trim to remove trailing spaces, replace to reduce multiple spaces to 1
        const trimmedSearchQuery = trimSearchQuery(searchQuery)
        // Sanitize search query to prevent XSS attacks
        const sanitizedQuery = InputSanitizationUtils.sanitizeSearchQuery(trimmedSearchQuery)
        
        // Log sanitization if dangerous content was detected
        const wasSanitized = trimmedSearchQuery !== sanitizedQuery;
        if (wasSanitized) {
            InputSanitizationUtils.logSanitization(trimmedSearchQuery, sanitizedQuery, 'LocationSearch-debounced');
        }
        
        // If content was sanitized, always proceed with search (even if empty)
        // This ensures we log the search attempt and show appropriate UI feedback
        const shouldProceedWithSearch = isSearchQueryValidMethod(sanitizedQuery) || wasLastInputSanitized;
        
        if (shouldProceedWithSearch) {
            const isDuplicate = isSearchQuerySameAsLast(sanitizedQuery) && !wasLastInputSanitized;
            
            if (!isDuplicate) {
                // new search query
                lastTrimmedSearchQuery = sanitizedQuery
                locationSearchFullResult = await geoSearchUtils.searchLocation(sanitizedQuery);

                // Show error status if nothing found
                if (locationSearchFullResult.length == 0) {
                    setSearchFieldInError(true)
                }

                setSearchOptionsFromResult(locationSearchFullResult)
                setIsSearchOptionsLoading(false)

            } else {
                // Loading false - show previously used options (no change in options should happen)
                setIsSearchOptionsLoading(false)
            }
            
            // Reset sanitization flag after search completes
            setWasLastInputSanitized(false);
            // console.warn("Debounced ", searchQuery);
        }

    }

    const setSearchOptionsFromResult = (searchResult: SearchResult<RawResult>[]) => {
        const tmpSearchOptions = searchResult.map(r => (
            { id: r.raw.place_id, label: r.label }
        ))

        // This setting is not imediate - therefore use tmp constatns and not setFull and then read it to setOptions - does not provide latest info like that.
        setSearchOptions(tmpSearchOptions)
    }

    const searchOptionSelected = (option: ComboBoxOption) => {
        const selectedSearchResult = locationSearchFullResult.find(r => (option.id == r.raw.place_id))

        if (selectedSearchResult) {
            locationSelected(selectedSearchResult)
            setIsDropdownOpen(false)
        } else {
            setSearchFieldInError(true)
        }
    }

    const searchValueSubmit = async (searchValue: string) => {
        // User submited search - cancel active timeout, to avoid unnecesary location search
        clearTimeout(activeTimeoutId)

        const trimmedSearchQuery = trimSearchQuery(searchValue)
        // Sanitize search query to prevent XSS attacks
        const sanitizedQuery = InputSanitizationUtils.sanitizeSearchQuery(trimmedSearchQuery)
        
        // Log sanitization if dangerous content was detected
        const wasSanitized = trimmedSearchQuery !== sanitizedQuery;
        if (wasSanitized) {
            InputSanitizationUtils.logSanitization(trimmedSearchQuery, sanitizedQuery, 'LocationSearch-submit');
        }

        // If content was sanitized, always proceed with search (even if empty)
        // This ensures we log the search attempt and show appropriate UI feedback
        const shouldProceedWithSearch = isSearchQueryValidMethod(sanitizedQuery) || wasSanitized;
        
        if (!shouldProceedWithSearch) {
            return
        }

        // new search query
        lastTrimmedSearchQuery = sanitizedQuery
        locationSearchFullResult = await geoSearchUtils.searchLocation(sanitizedQuery);

        // Update search field
        setSearchOptionsFromResult(locationSearchFullResult)
        setIsSearchOptionsLoading(false)

        if (locationSearchFullResult && locationSearchFullResult.length > 0) {
            locationSelected(locationSearchFullResult[0])

        } else {
            // Nothing found! Show error
            // This happens when name is quickly passed and enter hit:
            setSearchFieldInError(true)
            setSearchOptions([])
            // Reset last search query
            lastTrimmedSearchQuery = ""
            setSearchQuery("")
        }
    }

    const locationSelected = (locationSearchResult: SearchResult<RawResult>) => {
        onSearchLocationSelected(locationSearchResult)
    }

    const searchValueChanged = (event: ChangeEvent<HTMLInputElement>) => {
        const rawSearchQuery = event.target.value;
        
        // Sanitize the input immediately for display
        const trimmedSearchQuery = trimSearchQuery(rawSearchQuery);
        const sanitizedQuery = InputSanitizationUtils.sanitizeSearchQuery(trimmedSearchQuery);
        
        // Log sanitization if dangerous content was detected
        const wasSanitized = rawSearchQuery !== sanitizedQuery;
        if (wasSanitized) {
            InputSanitizationUtils.logSanitization(rawSearchQuery, sanitizedQuery, 'LocationSearch');
        }
        
        // Track if the last input was sanitized for debounced search logic
        setWasLastInputSanitized(wasSanitized);
        
        const isValid: boolean = isSearchQueryValidMethod(sanitizedQuery)

        if (isValid) {
            setSearchFieldInError(false)

            // Only switch to 'loading' and reset search options if search query differs from last
            const isDuplicate: boolean = isSearchQuerySameAsLast(sanitizedQuery)
            if (!isDuplicate) {
                // Reset pre-loaded locations in case search query is actually different from last time
                setIsSearchOptionsLoading(true)
                setSearchOptions([])
                setSearchQuery(sanitizedQuery)
            }
        } else {
            setSearchQuery(sanitizedQuery)
        }

    }

    const trimSearchQuery = (query: string): string => {
        return query.trim().replace(/ +/g, ' ');
    }

    const isSearchQuerySameAsLast = (searchQuery: string): boolean => {
        const trimmedSearchQuery = trimSearchQuery(searchQuery)

        return (lastTrimmedSearchQuery == trimmedSearchQuery)
    }

    const isSearchQueryValidMethod = (searchQuery: string): boolean => {
        const trimmedSearchQuery = trimSearchQuery(searchQuery)
        const isValid = trimmedSearchQuery != null && trimmedSearchQuery.length > 2

        if (!isValid) {
            setIsSearchOptionsLoading(false)
            setSearchOptions([])
            // Reset last search value
            lastTrimmedSearchQuery = ""
            setSearchQuery("")

            // Invalid (too short) is shown in empty message
            setSearchFieldInError(false)
        }

        setIsSearchQueryValid(isValid)

        return isValid
    }

    const optionFilterShowAll = (): boolean => {
        return true;
    }

    const handleArrowClick = () => {
        // Focus the input field to show the dropdown
        if (autocompleteRef.current) {
            autocompleteRef.current.focus();
            // Also trigger a click event to ensure dropdown opens
            autocompleteRef.current.click();
            setIsDropdownOpen(true);
        }
    }

    const handleDropdownOpen = () => {
        setIsDropdownOpen(true);
    }

    const handleDropdownClose = () => {
        setIsDropdownOpen(false);
    }

    return (
        <>
            <Flex
                className="location-search-flex"
                direction="column"
                alignItems="center"
                gap="0"
            >
                <Autocomplete
                    ref={autocompleteRef}
                    className={"location-search-field" + (searchFieldInError ? " in-error" : "")}
                    label=""
                    placeholder={t('location.type-address-here')}
                    hasSearchButton={false}
                    hasSearchIcon={true}
                    size="large"
                    onChange={searchValueChanged}
                    onSubmit={searchValueSubmit}
                    options={searchOptions}
                    onFocus={handleDropdownOpen}
                    onBlur={handleDropdownClose}
                    optionFilter={optionFilterShowAll}
                    menuSlots={{
                        LoadingIndicator: (
                            <Flex alignItems="center" gap="0.25rem">
                                <Loader />
                                {t('location.loading-locations')}
                            </Flex>
                        ),
                        Empty: isSearchQueryValid ? t('location.no-locations-found') : t('location.type-at-least-3-chars')
                        // Empty: "</>"
                    }}
                    isLoading={isSearchOptionsLoading}
                    onSelect={searchOptionSelected}
                />
                {searchOptions && (searchOptions.length > 0) && !isDropdownOpen &&
                    <FaAngleDown
                        className="location-search-icon"
                        color="black"
                        size="1.5rem"
                        onClick={handleArrowClick}
                    />
                }
            </Flex>

        </>
    )
}

export default LocationSearch